/**
 * Target of an SBOM upload onto one product (DEV-107): either a NEW official
 * version or an UPDATE of an existing official one. Custom versions are not
 * offered — they are updated through the upload on their product instance.
 *
 * The panel owns loading the product and its versions; the page only receives
 * the resulting request (or null while the choice is incomplete).
 */

import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';

import { Banner } from '../../components/Banner';
import Spinner from '../../components/Spinner';
import { errorMessage, fetchCmdbCard, fetchCmdbCards } from '../../lib/api';
import type { CmdbCardRow, ImportTargetRequest } from '../../lib/types';

export interface ProductTargetState {
  productName: string | null;
  request: ImportTargetRequest | null;
}

interface ImportTargetPanelProps {
  productId: number;
  /** Opened from a version row: update that official version. */
  initialVersionId?: number | null;
  disabled: boolean;
  onChange: (state: ProductTargetState) => void;
}

function versionLabel(row: CmdbCardRow): string {
  const nr = row.values.VersionNr;
  return typeof nr === 'string' && nr !== '' ? nr : row.label;
}

export function ImportTargetPanel({
  productId, initialVersionId = null, disabled, onChange,
}: ImportTargetPanelProps): ReactNode {
  const idPrefix = useId();
  const [product, setProduct] = useState<CmdbCardRow | null>(null);
  const [versions, setVersions] = useState<readonly CmdbCardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mode, setMode] = useState<'new' | 'update'>(initialVersionId !== null ? 'update' : 'new');
  const [versionNr, setVersionNr] = useState('');
  const [predecessorId, setPredecessorId] = useState<number | null>(null);
  const [versionId, setVersionId] = useState<number | null>(initialVersionId);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    Promise.all([fetchCmdbCard('ProduktType', productId), fetchCmdbCards('ProduktVersion')])
      .then(([productRow, versionList]) => {
        if (cancelled) return;
        setProduct(productRow);
        setVersions(
          versionList.rows
            .filter((row) => row.values.ProduktTypeID === productId)
            .filter((row) => row.values.VersionType !== 'CUSTOM')
            .sort((a, b) => versionLabel(a).localeCompare(versionLabel(b), 'de-DE', { numeric: true })),
        );
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(errorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  const taken = useMemo(
    () => new Set(versions.map((row) => versionLabel(row).trim().toLowerCase())),
    [versions],
  );
  const trimmedNr = versionNr.trim();
  const duplicate = mode === 'new' && trimmedNr !== '' && taken.has(trimmedNr.toLowerCase());

  const request = useMemo<ImportTargetRequest | null>(() => {
    if (product === null) return null;
    if (mode === 'new') {
      if (trimmedNr === '' || duplicate) return null;
      return {
        target: 'product', product_type_id: productId, mode,
        version_nr: trimmedNr, predecessor_id: predecessorId ?? undefined,
      };
    }
    if (versionId === null) return null;
    return { target: 'product', product_type_id: productId, mode, version_id: versionId };
  }, [product, mode, trimmedNr, duplicate, predecessorId, versionId, productId]);

  const productName = product?.label ?? null;
  useEffect(() => {
    onChange({ productName, request });
  }, [productName, request, onChange]);

  if (loading) {
    return (
      <Banner kind="info">
        <Spinner /> Produkt und Versionen werden geladen …
      </Banner>
    );
  }
  if (loadError !== null || product === null) {
    return <Banner kind="error">Produkt konnte nicht geladen werden: {loadError ?? 'unbekannt'}</Banner>;
  }

  return (
    <section className="imp-target" aria-labelledby={`${idPrefix}-title`}>
      <h2 id={`${idPrefix}-title`}>Ziel der SBOM</h2>
      <div className="seg imp-target-mode" role="radiogroup" aria-label="Upload-Ziel">
        <button
          type="button"
          role="radio"
          aria-checked={mode === 'new'}
          className={mode === 'new' ? 'on' : undefined}
          disabled={disabled}
          onClick={() => setMode('new')}
        >
          Neue Version anlegen
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={mode === 'update'}
          className={mode === 'update' ? 'on' : undefined}
          disabled={disabled || versions.length === 0}
          title={versions.length === 0 ? 'Das Produkt hat noch keine offizielle Version.' : undefined}
          onClick={() => setMode('update')}
        >
          Bestehende Version aktualisieren
        </button>
      </div>

      {mode === 'new' ? (
        <div className="imp-target-fields">
          <label htmlFor={`${idPrefix}-nr`}>
            Versionsnummer
            <input
              id={`${idPrefix}-nr`}
              type="text"
              value={versionNr}
              disabled={disabled}
              required
              aria-invalid={duplicate || undefined}
              aria-describedby={duplicate ? `${idPrefix}-nr-error` : undefined}
              onChange={(event) => setVersionNr(event.target.value)}
              placeholder="z. B. 2.0"
            />
          </label>
          {duplicate ? (
            <p id={`${idPrefix}-nr-error`} className="imp-target-error">
              Version {trimmedNr} existiert bereits — wählen Sie „Bestehende Version aktualisieren“.
            </p>
          ) : null}
          <label htmlFor={`${idPrefix}-pred`}>
            Vorgängerversion (optional)
            <select
              id={`${idPrefix}-pred`}
              value={predecessorId ?? ''}
              disabled={disabled}
              onChange={(event) => setPredecessorId(event.target.value === '' ? null : Number(event.target.value))}
            >
              <option value="">Keine</option>
              {versions.map((row) => (
                <option key={row.id} value={row.id}>{versionLabel(row)}</option>
              ))}
            </select>
          </label>
        </div>
      ) : (
        <div className="imp-target-fields">
          <label htmlFor={`${idPrefix}-version`}>
            Version
            <select
              id={`${idPrefix}-version`}
              value={versionId ?? ''}
              disabled={disabled}
              required
              onChange={(event) => setVersionId(event.target.value === '' ? null : Number(event.target.value))}
            >
              <option value="" disabled>Version wählen …</option>
              {versions.map((row) => (
                <option key={row.id} value={row.id}>{versionLabel(row)}</option>
              ))}
            </select>
          </label>
        </div>
      )}
    </section>
  );
}
